import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  isChunkError: boolean
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.name === 'ChunkLoadError'
  )
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false }

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ChunkErrorBoundary]', error, info)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isChunkError) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-background">
          <div className="text-center space-y-4 max-w-sm px-6">
            <div className="text-4xl">🔄</div>
            <h2 className="text-xl font-semibold text-foreground">עדכון זמין</h2>
            <p className="text-muted-foreground text-sm">
              גרסה חדשה של האפליקציה זמינה. רענן את הדף כדי להמשיך.
            </p>
            <button
              onClick={this.handleReload}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
            >
              רענן עכשיו
            </button>
          </div>
        </div>
      )
    }

    // Generic error fallback
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-sm px-6">
          <div className="text-4xl">⚠️</div>
          <h2 className="text-xl font-semibold text-foreground">משהו השתבש</h2>
          <p className="text-muted-foreground text-sm">
            אירעה שגיאה בלתי צפויה. נסה לרענן את הדף.
          </p>
          <button
            onClick={this.handleReload}
            className="mt-2 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            רענן
          </button>
        </div>
      </div>
    )
  }
}
